import {
  CourierDeliveryScheduleListResponseType,
  SupplierRelationshipFulfillmentType,
  FulfillmentTypeListResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  CountryCodeDictionary,
  daysOfWeek,
  findLatestPreOrderAvailableFromDate,
  formatCurrency,
  getNextCourierDispatchAndDeliveryDates,
  getNextStartDate,
  pluralize,
} from "@sellar-io/common";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { Icon, SpinnerLegacy } from "../../../components";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { FulfillmentTypeStartingPrice } from "../../../components/fulfillmentTypeCards/FulfillmentTypeStartingPrice";
import { DeliveryChargeCapToolTip } from "../../../components/deliveryChargeToolTip/DeliveryChargeCapToolTip";
import { ReactNode, useState } from "react";
import { getNextDirectDeliveryDate } from "../../order/helpers/DeliveryDateHelpers/getNextDeliveryDate";
import dayjs from "dayjs";
import { useCourierDeliveryScheduleList } from "../../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleList";
import { useBasketContext } from "../../../hooks";
import { useStockListOrderSummaryProps } from "../useStockListOrderSummaryProps";
import { useSupplierStorefrontV2 } from "../../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import { useFulfillmentTypeEligibility } from "../../../hooks/useFulfillmentTypeEligibility";
import { useFulfillmentTypeAutoSelect } from "../../../hooks/useFulfillmentTypeAutoSelect";
import { useIneligibleForCourierStore } from "../../../hooks/useIneligibleForCourierStore";
import { CourierUnavailableSelector } from "./CourierUnavailableSelector";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { OrderInformationButton } from "../checkoutV2/lib/Checkout";
import { useCurrentDate, Badge, Button, H5 } from "@sellar-io/components";
import { StorefrontDeliveryOptionsDialog } from "../categoryPage/Storefront/StorefrontDialogs/StorefrontDeliveryOptionsDialog";
import { CheckoutSelectedFulfillmentType } from "..";
import { useDistributionDeliveryEstimate } from "~/hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";

type FulfillmentTypeRadioSelectorV2Props = {
  deliveryArea: SupplierStorefrontV2GetResponseType["DeliveryArea"] | undefined;
  directDeliveryArea:
    | SupplierStorefrontV2GetResponseType["DeliveryArea"]
    | undefined;
  sdDeliveryArea:
    | SupplierStorefrontV2GetResponseType["sdDeliveryArea"]
    | undefined;
  supplierRelationshipFulfillmentType:
    | SupplierRelationshipFulfillmentType
    | undefined;
  value: CheckoutSelectedFulfillmentType;
  supplier: SupplierGetResponseType;
  order: ReturnType<typeof useBasketContext>["0"]["order"];
  priceTierLink: string | undefined;
  buyerPostcode: string;
  onChange: (data: {
    fulfillmentType: CheckoutSelectedFulfillmentType;
  }) => Promise<void> | void;
  renderFooter?: (params: {
    eligibleFulfillmentTypes: CheckoutSelectedFulfillmentType[];
  }) => ReactNode;
};

/**
 * Used exclusively for the new checkout flow.
 * It will eventually replace the old FulfillmentTypeRadioSelector
 */
export const FulfillmentTypeRadioSelectorV2 = ({
  supplierRelationshipFulfillmentType,
  deliveryArea,
  directDeliveryArea,
  sdDeliveryArea,
  supplier,
  order,
  priceTierLink,
  value,
  buyerPostcode,
  onChange,
  renderFooter,
}: FulfillmentTypeRadioSelectorV2Props) => {
  const currentDate = useCurrentDate();
  const { hasItemIneligibleForCourier } = useIneligibleForCourierStore();

  const [selectorActive, setSelectorActive] = useState(false);

  const palletOverrideEnabled = useRemoteFeatureFlags().isActive(
    "courierHaulierEnforcement",
  );

  const eligibility = useFulfillmentTypeEligibility({
    connection: {
      fulfillmentType: supplierRelationshipFulfillmentType ?? null,
    },
    directDeliveryArea,
    sdDeliveryArea,
    allowCollectionOnCheckout: supplier.allowCollectionOnCheckout,
    eligibleForHaulierOverride:
      hasItemIneligibleForCourier && palletOverrideEnabled,
  });

  const {
    isEligibleForCollection,
    isEligibleForCourier,
    isEligibleForDirect,
    isEligibleForHaulier,
    isEligibleForDistribution,
  } = eligibility;

  useFulfillmentTypeAutoSelect({
    isEligibleForDistribution,
    isEligibleForDirect,
    value,
    supplierId: supplier.id,
    onUpdate: (fulfillmentType) => onChange({ fulfillmentType }),
  });

  const noConnectionValue = isEligibleForDistribution
    ? "sellarDistribution"
    : isEligibleForDirect
      ? "direct"
      : "courier";

  const initialValue = value ?? noConnectionValue;

  const [updatedValue, setUpdatedValue] =
    useState<CheckoutSelectedFulfillmentType>(initialValue);

  const fulfillmentTypeCourierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "courier",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
    },
    {
      enabled: isEligibleForCourier,
    },
  );

  const fulfillmentTypeDirectQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "direct",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
    },
    {
      enabled: isEligibleForDirect,
    },
  );

  const fulfillmentTypeDistributionQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "sellarDistribution",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
    },
    {
      enabled: isEligibleForDistribution,
    },
  );

  const fulfillmentTypeHaulierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "haulier",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
    },
    {
      enabled: isEligibleForHaulier || palletOverrideEnabled,
    },
  );

  const courierDeliveryScheduleQuery = useCourierDeliveryScheduleList(
    {
      query: [
        {
          value: supplier.id,
          type: "SupplierId",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
      sortDir: "desc",
    },
    {
      enabled: !!supplier.id,
    },
  );

  const supplierId = supplier.id;

  const directDeliveryData = useGetDeliveryData({
    supplierId,
    fulfillmentType: "direct",
    order,
    priceTierLink,
  });

  const distributionDeliveryData = useGetDeliveryData({
    supplierId,
    fulfillmentType: "sellarDistribution",
    order,
    priceTierLink,
  });

  const haulierDeliveryData = useGetDeliveryData({
    supplierId,
    fulfillmentType: "haulier",
    order,
    priceTierLink,
  });

  const courierDeliveryData = useGetDeliveryData({
    supplierId,
    fulfillmentType: "courier",
    order,
    priceTierLink,
  });

  const fulfillmentTypeCourierQueryLoading =
    fulfillmentTypeCourierQuery.isPending && isEligibleForCourier;
  const fulfillmentTypeHaulierQueryLoading =
    fulfillmentTypeHaulierQuery.isPending && isEligibleForHaulier;

  const isFulfillmentSelectorLoading =
    fulfillmentTypeCourierQueryLoading || fulfillmentTypeHaulierQueryLoading;

  const courierFulfillmentType =
    fulfillmentTypeCourierQuery.data?.pages[0]?.[0];

  const haulierFulfillmentType =
    fulfillmentTypeHaulierQuery.data?.pages[0]?.[0];

  const directFulfillmentType = fulfillmentTypeDirectQuery.data?.pages[0]?.[0];

  const distributionFulfillmentType =
    fulfillmentTypeDistributionQuery.data?.pages[0]?.[0];

  const disableCourier =
    hasItemIneligibleForCourier &&
    haulierFulfillmentType &&
    palletOverrideEnabled;

  const outOfOfficeReturnDate = supplier.SupplierOutOfOffice?.outOfOffice
    ? supplier.SupplierOutOfOffice.returnDate
    : null;

  const orderItems =
    order?.OrderItems?.map((orderItem) => ({
      availableFrom: orderItem.Product.availableFrom,
      quantity: orderItem.quantity,
    })) ?? [];

  const productIds =
    order?.OrderItems?.map((orderItem) => orderItem.ProductId) ?? [];

  const latestAvailablePreOrderFromDate =
    findLatestPreOrderAvailableFromDate(orderItems);

  const startDateReference = getNextStartDate({
    latestAvailablePreOrderFromDate,
    outOfOfficeReturnDate,
    courierDeliveryScheduleStartDate: null,
    currentDate,
  });

  const collectionAddress =
    supplier.CollectionFromAddress || supplier.Business.DeliveryAddress;
  const [moreInfoDialogActive, setMoreInfoDialogActive] = useState(false);

  const handleConfirmFulfillmentType = async () => {
    await onChange({ fulfillmentType: updatedValue });
    setSelectorActive(false);
  };

  if (isFulfillmentSelectorLoading) return <SpinnerLegacy />;

  const getDetails = () => {
    let price: string;
    switch (initialValue) {
      case "sellarDistribution":
        return {
          title: `Delivered by Sellar`,
          subtitle: (
            <SellarDistributionDeliveryDescriptionForNonCompact
              sdDeliveryArea={sdDeliveryArea}
              productIds={productIds}
              supplierId={supplier.id}
              buyerPostcode={buyerPostcode}
            />
          ),
          price: "FREE",
        };

      case "direct":
        price = `${formatCurrency(directDeliveryData.deliveryCharge)}${directDeliveryData.isDeliveryChargeCapped ? "" : " (ex. VAT)"}`;
        return {
          title: `Delivered by ${supplier.Business.name}`,
          subtitle: (
            <DirectDeliveryDescriptionForNonCompact
              startDateReference={startDateReference}
              deliveryArea={directDeliveryArea}
            />
          ),
          price,
        };

      case "courier":
        price = `${formatCurrency(courierDeliveryData.deliveryCharge)}${courierDeliveryData.isDeliveryChargeCapped ? "" : " (ex. VAT)"}`;
        return {
          title: "Courier delivery",
          subtitle: (
            <NonCompactCourierDeliveryDescription
              courierDeliveryScheduleQuery={courierDeliveryScheduleQuery}
              startDateReference={startDateReference}
              includeWeekend={deliveryArea?.includeWeekend ?? true}
            />
          ),
          price,
        };

      case "haulier":
        price = `${formatCurrency(haulierDeliveryData.deliveryCharge)}${haulierDeliveryData.isDeliveryChargeCapped ? "" : " (ex. VAT)"}`;
        return {
          title: palletOverrideEnabled ? "Pallet" : "Haulier",
          subtitle: "Typically delivered in 1-3 business days from dispatch",
          price,
        };

      case "collection":
        return {
          title: "Collection from supplier",
          subtitle: `Arrange a time with ${supplier.Business.name} during their opening hours to collect your order from their business address in ${collectionAddress.city}, ${collectionAddress.postalCode}`,
          price: "FREE",
        };
    }
  };

  const details = getDetails();

  if (supplier.onlyAllowCardPaymentWithDeposit) {
    return (
      <>
        <div className="flex flex-col gap-2 rounded-lg bg-white p-4">
          <Badge icon={"FERRY"}>Sellar imports</Badge>
          <p>
            Your order will be shipped from {supplier.Business.name} in{" "}
            {supplier.Business.DeliveryAddress.city},{" "}
            {
              CountryCodeDictionary[
                supplier.Business.DeliveryAddress
                  .country as keyof typeof CountryCodeDictionary
              ]
            }{" "}
            to our warehouse.
          </p>
          <div
            onClick={() => setMoreInfoDialogActive(true)}
            className={
              "m-0 flex cursor-pointer flex-row items-center gap-2 p-0 text-blue-800"
            }
          >
            <Icon icon={"OPEN_IN_NEW"} />
            <p className={"font-semibold"}>MORE INFO</p>
          </div>
        </div>

        <StorefrontDeliveryOptionsDialog
          active={moreInfoDialogActive}
          onDismiss={() => {
            setMoreInfoDialogActive(false);
          }}
          supplier={supplier}
          connection={{
            fulfillmentType: supplierRelationshipFulfillmentType ?? null,
          }}
          unAuthorisedUserPostcode={null}
          fulfillmentType={initialValue}
          order={{ ...order, RetailerId: 1 }}
        />
      </>
    );
  }

  return (
    <>
      <OrderInformationButton
        startIcon="TRUCK_OUTLINE"
        endIcon="PENCIL"
        endText={<b>{details.price}</b>}
        title={details.title}
        subtitle={details.subtitle}
        subtitleFallback=""
        onClick={() => setSelectorActive(true)}
        dialog={
          <Dialog
            active={selectorActive}
            onDismiss={() => setSelectorActive(false)}
          >
            <DialogNavigation
              title="Select delivery method"
              onClickLeftIcon={() => setSelectorActive(false)}
              leftIcon="CHEVRON_DOWN"
            />
            <DialogContent>
              <RadioGroup
                value={updatedValue}
                onChange={(value) => {
                  const newFulfillmentType =
                    value as CheckoutSelectedFulfillmentType;
                  setUpdatedValue(newFulfillmentType);
                }}
              >
                <RadioGroupContainer>
                  {isEligibleForDistribution && (
                    <RadioGroupOption
                      value="sellarDistribution"
                      className="p-4"
                    >
                      <NonCompactDeliveryOption
                        nbOrderItems={orderItems.length}
                        deliveryDataProps={distributionDeliveryData}
                        deliveryFulfillmentType={distributionFulfillmentType}
                        title={`Delivered by Sellar`}
                        description={
                          <SellarDistributionDeliveryDescriptionForNonCompact
                            sdDeliveryArea={sdDeliveryArea}
                            productIds={productIds}
                            supplierId={supplier.id}
                            buyerPostcode={buyerPostcode}
                          />
                        }
                      />
                    </RadioGroupOption>
                  )}
                  {isEligibleForDirect && (
                    <RadioGroupOption value="direct" className="p-4">
                      <NonCompactDeliveryOption
                        nbOrderItems={orderItems.length}
                        deliveryDataProps={directDeliveryData}
                        deliveryFulfillmentType={directFulfillmentType}
                        title={`Delivered by ${supplier.Business.name}`}
                        description={
                          <DirectDeliveryDescriptionForNonCompact
                            startDateReference={startDateReference}
                            deliveryArea={directDeliveryArea}
                          />
                        }
                      />
                    </RadioGroupOption>
                  )}
                  {isEligibleForCourier && (
                    <>
                      {disableCourier ? (
                        <CourierUnavailableSelector
                          updateFulfillmentType={() => {
                            onChange({ fulfillmentType: "courier" });
                          }}
                        />
                      ) : (
                        <RadioGroupOption value="courier" className="p-4">
                          <NonCompactDeliveryOption
                            nbOrderItems={orderItems.length}
                            deliveryDataProps={courierDeliveryData}
                            deliveryFulfillmentType={courierFulfillmentType}
                            title="Courier delivery"
                            description={
                              <NonCompactCourierDeliveryDescription
                                courierDeliveryScheduleQuery={
                                  courierDeliveryScheduleQuery
                                }
                                startDateReference={startDateReference}
                                includeWeekend={true}
                              />
                            }
                          />
                        </RadioGroupOption>
                      )}
                    </>
                  )}
                  {(isEligibleForHaulier ||
                    !!haulierFulfillmentType?.charge) && (
                    <RadioGroupOption value="haulier" className="p-4">
                      <NonCompactDeliveryOption
                        nbOrderItems={orderItems.reduce(
                          (num, item) => num + item.quantity,
                          0,
                        )}
                        deliveryDataProps={haulierDeliveryData}
                        deliveryFulfillmentType={haulierFulfillmentType}
                        title={palletOverrideEnabled ? "Pallet" : "Haulier"}
                        description="Typically delivered in 1-3 business days from dispatch"
                      />
                    </RadioGroupOption>
                  )}
                  {isEligibleForCollection && (
                    <RadioGroupOption value="collection" className="p-4">
                      <div className="flex w-full flex-col">
                        <div className="flex flex-row justify-between">
                          <H5>Collection from supplier</H5>
                          <H5>FREE</H5>
                        </div>
                        <p>
                          Arrange a time with {supplier.Business.name} during
                          their opening hours to collect your order from their
                          business address in {collectionAddress.city},{" "}
                          {collectionAddress.postalCode}
                        </p>
                      </div>
                    </RadioGroupOption>
                  )}
                </RadioGroupContainer>
              </RadioGroup>
            </DialogContent>
            <DialogCTA>
              <Button onClick={handleConfirmFulfillmentType}>Confirm</Button>
            </DialogCTA>
          </Dialog>
        }
      />
      {renderFooter?.({
        eligibleFulfillmentTypes: [
          ...new Set(
            [
              isEligibleForCourier && ("courier" as const),
              isEligibleForDirect && ("direct" as const),
              isEligibleForDistribution && ("sellarDistribution" as const),
              isEligibleForHaulier && ("haulier" as const),
            ].filter(Boolean),
          ),
        ],
      })}
    </>
  );
};

const useGetDeliveryData = ({
  supplierId,
  order,
  fulfillmentType,
  priceTierLink,
}: {
  supplierId: number;
  order: ReturnType<typeof useBasketContext>["0"]["order"];
  fulfillmentType: CheckoutSelectedFulfillmentType;
  priceTierLink: string | undefined;
}) => {
  const supplierStorefrontQuery = useSupplierStorefrontV2(supplierId, {
    PriceTierLink: priceTierLink,
    fulfillmentType,
  });
  const supplierStorefront = supplierStorefrontQuery.data;

  const deliveryChargeRules =
    supplierStorefront?.FulfillmentType.DeliveryChargeRules;

  const deliveryChargeInput = supplierStorefront?.FulfillmentType.charge;

  const deliveryChargeCap =
    supplierStorefront?.FulfillmentType.deliveryChargeCap;

  const fulfillmentTypeContainerDeliveryCharges =
    supplierStorefront?.FulfillmentType.FulfillmentTypeContainerDeliveryCharges;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const containerTypeDeliveryChargeRules =
    supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ?? [];

  const {
    apiValues: { deliveryCharge, isDeliveryChargeCapped },
    deliveryChargeWithoutRules,
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules,
    deliveryCharge: deliveryChargeInput,
    deliveryChargeCap,
    fulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules,
    freeDeliveryBasketSubtotal,
  });

  return {
    deliveryCharge,
    isDeliveryChargeCapped,
    deliveryChargeWithoutRules,
  };
};

type CourierDeliveryDescriptionProps = {
  courierDeliveryScheduleQuery: ReturnType<
    typeof useCourierDeliveryScheduleList<CourierDeliveryScheduleListResponseType>
  >;
  startDateReference: Date | null;
  includeWeekend: boolean;
};
const NonCompactCourierDeliveryDescription = ({
  courierDeliveryScheduleQuery,
  startDateReference,
}: CourierDeliveryDescriptionProps) => {
  const courierDeliverySchedule =
    courierDeliveryScheduleQuery.data?.pages[0]?.[0];

  if (!courierDeliverySchedule) {
    return "Typically delivered in 1-3 business days from dispatch";
  }

  const nextCourierDispatchAndDeliveryDates =
    getNextCourierDispatchAndDeliveryDates({
      orderCutOffTime: dayjs(courierDeliverySchedule.orderCutOffTime)
        .utc()
        .format("HH:mm"),
      courierDaysSchedule:
        courierDeliverySchedule.CourierDeliveryScheduleDay.map(
          (CourierDeliveryScheduleDay) => CourierDeliveryScheduleDay.day,
        ).map((day: daysOfWeek) => ({ day })),
      dispatchDaysAfterConfirmation:
        courierDeliverySchedule.dispatchDaysAfterConfirmation,
      deliveryDaysAfterDispatch:
        courierDeliverySchedule.deliveryDaysAfterDispatch,
      startDateReference: startDateReference ?? new Date(),
      allowSaturdayDelivery: courierDeliverySchedule.allowSaturdayDelivery,
    });

  if (!nextCourierDispatchAndDeliveryDates) return "";

  return `Delivered by ${nextCourierDispatchAndDeliveryDates.estimatedDeliveryDate.format("DD/MM/YYYY")}`;
};

interface DirectDeliveryDescriptionForNonCompactProps extends Pick<
  FulfillmentTypeRadioSelectorV2Props,
  "deliveryArea"
> {
  startDateReference: Date | null;
}
const DirectDeliveryDescriptionForNonCompact = ({
  deliveryArea,
  startDateReference,
}: DirectDeliveryDescriptionForNonCompactProps) => {
  if (!deliveryArea || deliveryArea.DeliveryAreaDays.length === 0) return "";

  const nextDirectDeliveryDates = getNextDirectDeliveryDate({
    deliveryArea,
    startDateReference: startDateReference ?? new Date(),
  });

  const formattedNextDirectDeliveryDate = dayjs(
    nextDirectDeliveryDates?.deliveryDate,
  ).format("DD/MM/YYYY");

  return `Delivered by ${formattedNextDirectDeliveryDate}`;
};

interface SellarDistributionDeliveryDescriptionForNonCompactProps extends Pick<
  FulfillmentTypeRadioSelectorV2Props,
  "sdDeliveryArea"
> {
  supplierId: number;
  productIds: number[];
  buyerPostcode: string;
}
const SellarDistributionDeliveryDescriptionForNonCompact = ({
  sdDeliveryArea,
  supplierId,
  productIds,
  buyerPostcode,
}: SellarDistributionDeliveryDescriptionForNonCompactProps) => {
  const { nextDeliveryDate: sdDeliveryDate } = useDistributionDeliveryEstimate({
    supplierId,
    buyerPostcode,
    productIds,
  });

  if (!sdDeliveryArea || sdDeliveryArea.DeliveryAreaDays.length === 0)
    return "";

  return `Delivered by ${dayjs(sdDeliveryDate).format("DD/MM/YYYY")}`;
};

type NonCompactDeliveryOptionProps = {
  deliveryFulfillmentType: FulfillmentTypeListResponseType[number] | undefined;
  title: ReactNode;
  description: ReactNode;
  deliveryDataProps: ReturnType<typeof useGetDeliveryData>;
  nbOrderItems: number;
};

const NonCompactDeliveryOption = ({
  title,
  description,
  nbOrderItems,
  deliveryFulfillmentType,
  deliveryDataProps: { deliveryCharge, isDeliveryChargeCapped },
}: NonCompactDeliveryOptionProps) => {
  const hasDeliveryCharge = deliveryCharge > 0;

  const palletOverrideEnabled = useRemoteFeatureFlags().isActive(
    "courierHaulierEnforcement",
  );

  const deliveryChargeCappedMessage = (
    <DeliveryChargeCapToolTip>
      <div className="flex flex-col">
        <div className="inline-flex cursor-pointer items-center gap-1 font-bold">
          <Icon icon="INFORMATION_OUTLINE" size={0.65} />
          {formatCurrency(deliveryCharge)}
        </div>
      </div>
    </DeliveryChargeCapToolTip>
  );

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-row justify-between">
        <H5>{title}</H5>

        {hasDeliveryCharge &&
          isDeliveryChargeCapped &&
          deliveryChargeCappedMessage}

        {hasDeliveryCharge && !isDeliveryChargeCapped && (
          <H5>{formatCurrency(deliveryCharge)} (ex. VAT)</H5>
        )}

        {!hasDeliveryCharge && (
          <FulfillmentTypeStartingPrice
            fulfillmentTypeCharge={deliveryFulfillmentType?.charge}
            orderItemLength={nbOrderItems}
            fulfillmentType={deliveryFulfillmentType?.fulfillmentType}
            hasPerItemCharges={
              deliveryFulfillmentType?.FulfillmentTypeContainerDeliveryCharges.some(
                (containerCharge) => containerCharge.charge > 0,
              ) ?? false
            }
          />
        )}
      </div>
      <p>{description}</p>
      {palletOverrideEnabled &&
        deliveryFulfillmentType?.fulfillmentType === "haulier" && (
          <span className="mt-2 font-semibold">{`${nbOrderItems} ${pluralize("item", nbOrderItems)} • ${formatCurrency(deliveryCharge / nbOrderItems)} each`}</span>
        )}
    </div>
  );
};
